import React, { useCallback, useMemo } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';

/**
 * US phone input with `XXX-XXX-XXXX` mask — тот же вид, что лежит в БД
 * (Employee.Phone / Temps.Phone), чтобы номера уходили туда единообразно.
 * Наружу (value/onChange) по-прежнему отдаёт 10 голых цифр.
 *
 * Props:
 *  - value: 10-digit string (or shorter if user is still typing)
 *  - onChange: (digits, isComplete) => void
 *  - label, error, helperText, required, fullWidth, size — passed to MUI TextField
 *  - disabled
 */
export default function PhoneInput({
    value = '',
    onChange,
    label = 'Phone',
    helperText,
    error,
    required = false,
    fullWidth = true,
    size = 'medium',
    disabled = false,
}) {
    const display = useMemo(() => formatDisplay(value), [value]);

    const handleChange = useCallback((e) => {
        const raw = e.target.value.replace(/\D/g, '');
        const trimmed = raw.length > 10 ? raw.slice(-10) : raw;  // ignore extra digits beyond 10
        if (onChange) {
            onChange(trimmed, trimmed.length === 10);
        }
    }, [onChange]);

    const isComplete = value.length === 10;
    const showHelper = helperText !== undefined
        ? helperText
        : (isComplete ? null : 'Enter 10 digits, e.g. 864-263-2277');

    return (
        <TextField
            label={label}
            value={display}
            onChange={handleChange}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            required={required}
            fullWidth={fullWidth}
            size={size}
            disabled={disabled}
            error={!!error}
            helperText={error || showHelper}
            placeholder="XXX-XXX-XXXX"
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <PhoneIcon fontSize="small" />
                    </InputAdornment>
                ),
            }}
            inputProps={{
                maxLength: 12,  // XXX-XXX-XXXX = 12 chars
            }}
        />
    );
}

function formatDisplay(rawDigits) {
    if (!rawDigits) return '';
    const d = String(rawDigits).replace(/\D/g, '').slice(0, 10);
    if (d.length === 0) return '';
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

export function normalizePhone(input) {
    if (!input) return '';
    const d = String(input).replace(/\D/g, '');
    if (d.length === 11 && d.startsWith('1')) return d.slice(1);
    return d.length === 10 ? d : '';
}

export function formatPhone(digits) {
    return formatDisplay(digits);
}
